import React, { useState, useEffect } from 'react'
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormLabel,
  CInputGroup,
  CLoadingButton,
  CInputGroupText,
  CRow,
  CFormFeedback,
  CButton,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilArrowRight,
  cilCalendar,
  cilCheckCircle,
  cilDescription,
  cilPencil,
  cilShareAlt,
  cilShieldAlt,
  cilUser,
} from '@coreui/icons'
import { useNavigate } from 'react-router-dom'
import ClubService from 'src/service/ClubService'
import CommonService from 'src/service/CommonService'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ToastComponent from 'src/components/common/TaostComponent'
import Loader from 'src/components/Loader'
import GameSocial from './GameSocial'
import AboutGame from './AboutGame'
import GameType from './GameType'
import FeeInfo from './FeeInfo'
import BasicSetting from './BasicSetting'
import GameSpot from '../GameStructure/GameSpot'
import StepperBar from '../SetupProgress/StepperBar'
import SeasonDates from './SeasonDates'

const PRIVACY_LABELS = { 1: 'Public', 2: 'Private' }

const SectionCard = ({ icon, title, children }) => (
  <CCard className="mb-4">
    <CCardHeader className="d-flex align-items-center">
      <CIcon icon={icon} className="me-2" />
      <strong>{title}</strong>
    </CCardHeader>
    <CCardBody>{children}</CCardBody>
  </CCard>
)

const validationSchema = Yup.object().shape({
  email: Yup.string().required('Email is required').email('Email is invalid'),
  first_name: Yup.string().required('First Name is required').max(50),
  last_name: Yup.string().required('Last Name is required').max(50),
  phone: Yup.string().required('Phone is required'),
})

const ClubAboutGame = () => {
  const navigate = useNavigate()
  const [isLoading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [gameDetail, setGameDetail] = useState({})
  const [gameData, setGameData] = useState({})
  const [showEmail, setShowEmail] = useState(false)
  const [showName, setShowName] = useState(false)
  const [isTeamExists, setIsTeamExists] = useState(false)

  useEffect(() => {
    ClubService.getClubDetails().then((res) => {
      setLoading(true)
      if (res.status === 200) {
        setGameDetail(res.game_details)
        setGameData(res.game_details)
        if (res.user_details.show_email === 1) {
          setShowEmail(true)
        }
        if (res.user_details.show_name === 1) {
          setShowName(true)
        }
        setLoading(false)
      }
    })
    CommonService.gameStructureInfo().then((res) => {
      if (res.status === 200) setIsTeamExists(!!res.is_team_exists)
    })
  }, [])

  const buildFormData = (values) => {
    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'image') {
        if (value) formData.append('image', value)
        return
      }
      formData.append(key, value ?? '')
    })
    return formData
  }

  const saveChanges = async (values) => {
    setSaving(true)
    try {
      const res = await ClubService.updateAboutGamePage(buildFormData(values))
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        setGameDetail(res.data)
        setGameData(res.data)
        formik.setFieldValue('image', null)
        return true
      }
      ToastComponent(res.message, 'error')
      return false
    } catch (e) {
      ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
      return false
    } finally {
      setSaving(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      game_name: gameDetail?.game_name || '',
      image: null,
      message: gameDetail?.entry_message || '',
      game_type: gameDetail?.game_type || '',
      league_type: gameDetail?.league_type || '',
      timezone: gameDetail?.timezone || '',
      privacy: gameDetail?.game_privacy || '',
      user_number: gameDetail?.spots || '',
      first_name: gameDetail?.first_name || '',
      last_name: gameDetail?.last_name || '',
      email: gameDetail?.email || '',
      phone: gameDetail?.phone || '',
      facebook: gameDetail?.facebook || '',
      twitter: gameDetail?.twitter || '',
      instagram: gameDetail?.instagram || '',
      website: gameDetail?.website || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: saveChanges,
  })

  const handleEmailChange = () => setShowEmail((current) => !current)
  const handleNameChange = () => setShowName((current) => !current)

  const handleNext = async () => {
    const errors = await formik.validateForm()
    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    )
    if (Object.keys(errors).length > 0) {
      ToastComponent('Please fix the errors before continuing.', 'error')
      return
    }
    const ok = await saveChanges(formik.values)
    if (ok) {
      navigate('/game-structure')
    }
  }

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <StepperBar activeStepKey="about_game" />
      <CForm onSubmit={formik.handleSubmit}>
        <CRow>
          <CCol xs={12} lg={8}>
            <CRow>
              <CCol xs={12} md={6}>
                <CCard className="mb-4 h-100">
                  <CCardBody>
                    <SectionCard icon={cilPencil} title="Game Identity">
                      <BasicSetting
                        gameName={formik.values.game_name}
                        onGameNameChange={(value) => formik.setFieldValue('game_name', value)}
                        logo={gameDetail?.logo}
                        image={formik.values.image}
                        onImageChange={(file) => formik.setFieldValue('image', file)}
                      />
                    </SectionCard>

                    <SectionCard icon={cilDescription} title="Welcome Message">
                      <FeeInfo
                        entryMessage={formik.values.message}
                        setEntryMessage={(value) => formik.setFieldValue('message', value)}
                      />
                    </SectionCard>
                    <SectionCard icon={cilUser} title="Game Admin">
                      <CRow className="g-3">
                        <CCol md={6}>
                          <CFormLabel className="fw-bold" htmlFor="first_name">
                            First Name *
                          </CFormLabel>
                          <CInputGroup>
                            <CInputGroupText>
                              <input
                                type="checkbox"
                                checked={showName}
                                name="show_name"
                                defaultValue={1}
                                onChange={handleNameChange}
                              />
                            </CInputGroupText>
                            <input
                              type="text"
                              name="first_name"
                              className={
                                'form-control' +
                                (formik.errors.first_name && formik.touched.first_name
                                  ? ' is-invalid'
                                  : '')
                              }
                              id="first_name"
                              placeholder="First Name"
                              value={formik.values.first_name}
                              onChange={formik.handleChange}
                            />
                            {formik.errors.first_name && formik.touched.first_name && (
                              <CFormFeedback invalid>{formik.errors.first_name}</CFormFeedback>
                            )}
                          </CInputGroup>
                          <small>
                            <i> ({`Tick to hide admin's Full Name from members`})</i>
                          </small>
                        </CCol>
                        <CCol md={6}>
                          <CFormLabel className="fw-bold" htmlFor="last_name">
                            Last Name *
                          </CFormLabel>
                          <input
                            type="text"
                            name="last_name"
                            className={
                              'form-control' +
                              (formik.errors.last_name && formik.touched.last_name
                                ? ' is-invalid'
                                : '')
                            }
                            id="last_name"
                            placeholder="Last Name"
                            value={formik.values.last_name}
                            onChange={formik.handleChange}
                          />
                          {formik.errors.last_name && formik.touched.last_name && (
                            <CFormFeedback invalid>{formik.errors.last_name}</CFormFeedback>
                          )}
                        </CCol>

                        <CCol md={6}>
                          <CFormLabel className="fw-bold" htmlFor="email">
                            Email *
                          </CFormLabel>
                          <CInputGroup>
                            <CInputGroupText>
                              <input
                                type="checkbox"
                                checked={showEmail}
                                defaultValue={1}
                                name="show_email"
                                onChange={handleEmailChange}
                              />
                            </CInputGroupText>
                            <input
                              type="text"
                              name="email"
                              className={
                                'form-control' +
                                (formik.errors.email && formik.touched.email ? ' is-invalid' : '')
                              }
                              id="email"
                              placeholder="Email"
                              value={formik.values.email}
                              onChange={formik.handleChange}
                            />
                            {formik.errors.email && formik.touched.email && (
                              <CFormFeedback invalid>{formik.errors.email}</CFormFeedback>
                            )}
                          </CInputGroup>
                          <small>
                            <i> ({`Tick to hide admin's Email from members`})</i>
                          </small>
                        </CCol>
                        <CCol md={6}>
                          <CFormLabel className="fw-bold" htmlFor="phone">
                            Phone *
                          </CFormLabel>
                          <input
                            type="text"
                            name="phone"
                            className={
                              'form-control' +
                              (formik.errors.phone && formik.touched.phone ? ' is-invalid' : '')
                            }
                            id="phone"
                            placeholder="Phone"
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                          />
                          {formik.errors.phone && formik.touched.phone && (
                            <CFormFeedback invalid>{formik.errors.phone}</CFormFeedback>
                          )}
                        </CCol>
                      </CRow>
                    </SectionCard>
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol xs={12} md={6}>
                <CCard className="mb-4 h-100">
                  <CCardBody>
                    <SectionCard icon={cilCalendar} title="Game Details">
                      <GameType
                        gameType={formik.values.game_type}
                        setGameType={(value) => formik.setFieldValue('game_type', value)}
                        leagueType={formik.values.league_type}
                        setLeagueType={(value) => formik.setFieldValue('league_type', value)}
                        disabled={isTeamExists}
                      />
                      <hr />
                      <AboutGame
                        timezone={formik.values.timezone}
                        setTimezone={(value) => formik.setFieldValue('timezone', value)}
                      />
                      <hr />
                      <SeasonDates
                        startDate={gameDetail?.season_start_date}
                        endDate={gameDetail?.season_end_date}
                      />
                      <hr />
                      <CRow>
                        <CCol md={12}>
                          <CFormLabel className="fw-bold">Sport</CFormLabel>
                          <div>
                            <CBadge color="info">Cricket</CBadge>
                          </div>
                        </CCol>
                      </CRow>
                    </SectionCard>
                    <GameSpot
                      value={formik.values.user_number}
                      onChange={(value) => formik.setFieldValue('user_number', value)}
                    />
                    <SectionCard icon={cilShareAlt} title="Social Links">
                      <GameSocial
                        values={{
                          facebook: formik.values.facebook,
                          twitter: formik.values.twitter,
                          instagram: formik.values.instagram,
                          website: formik.values.website,
                        }}
                        onChange={(field, value) => formik.setFieldValue(field, value)}
                      />
                    </SectionCard>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CCol>

          <CCol xs={12} lg={4}>
            <CCard className="mb-4">
              <CCardHeader className="d-flex align-items-center">
                <CIcon icon={cilShieldAlt} className="me-2" />
                Preview
              </CCardHeader>
              <CCardBody>
                <div className="fw-bold mb-2">{formik.values.game_name}</div>
                <div className="mb-2">
                  <CBadge color="primary" className="me-1">
                    {PRIVACY_LABELS[formik.values.privacy] || 'Public'}
                  </CBadge>
                  <CBadge color="success">Cricket</CBadge>
                </div>
              </CCardBody>
            </CCard>

            <CCard className="mb-4">
              <CCardHeader>Tips</CCardHeader>
              <CCardBody>
                <div className="mb-2">
                  <CIcon icon={cilCheckCircle} className="text-success me-2" />
                  Use a clear and exciting welcome message
                </div>
                <div className="mb-2">
                  <CIcon icon={cilCheckCircle} className="text-success me-2" />
                  Add your logo for brand identity
                </div>
                <div className="mb-2">
                  <CIcon icon={cilCheckCircle} className="text-success me-2" />
                  You can update these details anytime
                </div>
                <div>
                  <CIcon icon={cilCheckCircle} className="text-success me-2" />
                  Visibility and entry settings can be changed later
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <CCard className="mb-4 mt-2">
          <CCardBody className="d-flex justify-content-between">
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => navigate('/game-home')}
              type="button"
            >
              <CIcon icon={cilArrowLeft} className="me-2" />
              Back to Game Home
            </CButton>
            <div className="d-flex gap-2">
              <CLoadingButton type="submit" color="secondary" variant="outline" loading={saving}>
                Save Changes
              </CLoadingButton>
              <CLoadingButton type="button" color="primary" loading={saving} onClick={handleNext}>
                Next: Game Structure
                <CIcon icon={cilArrowRight} className="ms-2" />
              </CLoadingButton>
            </div>
          </CCardBody>
        </CCard>
      </CForm>
    </>
  )
}

export default ClubAboutGame
